import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { BookOpen, Loader2 } from "lucide-react";
import { useMemo, useState } from "react";

import { Slide } from "@/components/card/slide";
import { KakaoDialog } from "@/components/dialog/kakao";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { requireAuth } from "@/middleware/auth";
import { usePackListQuery } from "@/queries/pack";

export const Route = createFileRoute("/pack/")({
	beforeLoad: async () => {
		await requireAuth();
	},
	component: RouteComponent,
});

function RouteComponent() {
	const queryOptions = usePackListQuery();
	const { data, isLoading, error } = useQuery(queryOptions);

	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [target, setTarget] = useState<string>("");
	const [search, setSearch] = useState<string>("");

	const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearch(e.target.value);
	};

	const filteredData = useMemo(() => {
		return data?.filter((pack) =>
			pack.name.toLowerCase().includes(search.toLowerCase()),
		);
	}, [data, search]);

	if (isLoading) {
		return (
			<div className="flex justify-center items-center h-full bg-gray-50">
				<div className="flex flex-col items-center gap-4">
					<Loader2 className="size-12 animate-spin text-blue-500" />
					<p className="text-lg text-gray-600 font-medium">
						슬라이드를 불러오는 중...
					</p>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex justify-center items-center h-full bg-gray-50">
				<div className="flex flex-col items-center gap-4">
					<div className="size-16 bg-red-100 rounded-full flex items-center justify-center">
						<BookOpen className="size-8 text-red-500" />
					</div>
					<p className="text-lg text-gray-600 font-medium">
						슬라이드를 불러올 수 없습니다.
					</p>
					<Button onClick={() => window.location.reload()}>다시 시도</Button>
				</div>
			</div>
		);
	}

	return (
		<>
			<div className="flex-1 container mx-auto px-4">
				<div className="flex items-center justify-end py-4 ml-3">
					<Input
						className="w-2/3 sm:w-1/3"
						placeholder="검색어를 입력해주세요."
						value={search}
						onChange={handleSearch}
					/>
				</div>
				{/* 메인 콘텐츠 */}
				<div>
					{filteredData && filteredData.length > 0 ? (
						<ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-6">
							{filteredData.map((pack) => (
								<Link
									key={pack.id}
									to="/pack/$pack_id"
									params={{ pack_id: pack.id }}
									className="block group"
								>
									<Slide
										type="pack"
										item={pack}
										setOpen={setIsOpen}
										setTarget={setTarget}
									/>
								</Link>
							))}
						</ul>
					) : (
						<div className="flex flex-col items-center justify-center py-20">
							<div className="size-24 bg-gray-50 rounded-full flex items-center justify-center mb-6">
								<BookOpen className="size-12 text-gray-400" />
							</div>
							<h3 className="text-2xl font-bold text-gray-900 mb-2">
								검색 결과가 없습니다
							</h3>
							<p className="text-gray-600 text-center max-w-md">
								검색어를 다시 확인해주세요.
							</p>
						</div>
					)}
				</div>
			</div>
			<KakaoDialog
				source_type="pack"
				target={target}
				hide_trigger
				open={isOpen}
				setOpen={setIsOpen}
			/>
		</>
	);
}
